<template>
  <!-- prettier-ignore -->
  <div :class="classes">
    <template v-if="isDisabled">
      <div class="action__unavailable">
        <span>{{ $t(title) }}&nbsp;</span>
        <ovp-icon
          v-tooltip="tooltip"
          art="light"
          name="question-circle"
        />
      </div>
    </template>

    <template v-else>
      <ovp-button
        :key="title"
        v-tooltip="tooltip"
        :disabled="isDisabled"
        :tiny="tiny"
        :plain="isPlain"
        :primary="isPrimary"
        :secondary="isSecondary"
        :loading="loading"
        @click="actionHandler">
        {{ $t(title) }}
      </ovp-button>
    </template>
  </div>
</template>

<script>
import { ProductHelper } from '@/helpers'

export default {
  name: 'Action',

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    personId: {
      type: String,
      required: true,
    },

    primary: {
      type: Boolean,
      default: false,
    },

    product: {
      type: Object,
      required: true,
    },

    selected: {
      type: Boolean,
      default: false,
    },

    tiny: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    classes() {
      return {
        action: true,
        'action--disabled': this.isDisabled,
      }
    },

    isMissingDoctorError() {
      return this.product.medicalOffice && !this.product.doctor
    },

    isDisabled() {
      return this.disabled || (!this.product.valid && !this.selected)
    },

    isPlain() {
      return this.disabled
    },

    isPrimary() {
      return this.primary && !this.isPlain && !this.disabled && !this.needsDoctor
    },

    isSecondary() {
      return !this.isPrimary && !this.isPlain
    },

    /**
     * Only show search doctor if the product is not selected.
     * If it's selected and the doctor is missing,
     * a warning message and action will be displayed in the product doctor.
     */
    needsDoctor() {
      return this.product.medicalOffice && !this.product.doctor && !this.product.selected
    },

    title() {
      if (!this.product.valid && !this.selected) {
        return 'product.unavailable'
      } else if (this.selected) {
        return this.getContent(this.product.id, 'actions.remove')
      } else if (!this.tiny && this.needsDoctor) {
        return this.$t('product.doctor.add')
      } else {
        return this.getContent(this.product.id, 'actions.add')
      }
    },

    tooltip() {
      if (this.isDisabled) {
        let content = this.product.messages
          ?.map(m => {
            return this.$t(`error.${m.key}`)
          })
          .join('; ')
          .trim()

        if (!content) {
          content = this.$t(this.getContent(this.product.id, 'unavailable'))
        }

        return {
          allowHTML: true,
          content,
        }
      } else {
        return false
      }
    },

    willReplace() {
      return ProductHelper.willReplace(this.personId, this.product.id)
    },
  },

  methods: {
    actionHandler() {
      if (this.isDisabled) return

      if (this.product.medicalOffice && this.needsDoctor) {
        this.$emit('search')
      } else if (!this.selected && this.product.valid) {
        const productId = this.willReplace
        this.$emit('add', { replace: !!productId, productId })
      } else if (this.selected) {
        this.$emit('remove')
      }
    },

    getContent(productId, key) {
      const productPath = `content.products.${productId}`
      return this.$te(`${productPath}.${key}`) ? `${productPath}.${key}` : `product.${key}`
    },
  },
}
</script>

<style name="mobile" scoped>
.action {
  button {
    margin-bottom: 0;
  }

  .button.plain {
    color: var(--c-black) !important;

    .icon {
      color: var(--c-black);
      margin: 0 0 0 5px;
    }
  }

  &--disabled button,
  &--disabled button:hover {
    opacity: 0.5 !important;
    cursor: not-allowed !important;
    border-color: transparent !important;
    background: none !important;
    color: var(--c-original-green) !important;
    fill: var(--c-original-green) !important;
  }

  &__unavailable {
    font-size: 15px;
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .action {
  }
}
</style>
